import React, { useState } from 'react';
import {
  CardContents,
  TitleContainer,
  CardTitle,
  CancelButton,
  FormError,
  InputContainer,
  AmountInput,
  PrimaryButton,
  ButtonContainer,
  SecondaryButton,
  Selector,
  InputBox,
} from 'common/components/cardStyles';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { useProducerWithThunks } from 'common/hooks/useProducer';

import { TransferChoices, defaultAPIRequestState } from 'api';
import { ClientTransfer } from 'common/state/banking/transfers/actions';
import {
  doAcceptClientTransfer,
  doUnacceptClientTransfer,
  doNewClientTransfer,
} from 'common/state/banking/transfers/thunkActions';
import {
  acceptClientTransferRecipe,
  newClientTransferRecipe,
  unacceptClientTransferRecipe,
} from 'common/state/banking/transfers/recipes';

interface DepositModal {
  selectedRow: ClientTransfer;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

interface DepositNewModal {
  choices: TransferChoices;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

export function DepositNewModal({
  choices,
  setShouldRefresh,
  closeModal,
}: DepositNewModal) {
  const { sender, currency, request, destination } = choices;

  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');

  // The currently selected request
  const [selectedRequest, setSelectedRequest] = useState<null | ElementType<
    typeof request
  >>(null);
  // The currently selected sender
  const [selectedSender, setSelectedSender] = useState<null | ElementType<
    typeof sender
  >>(null);
  // The currently selected destination
  const [selectedDestination, setSelectedDestination] =
    useState<null | ElementType<typeof destination>>(null);
  // The currently selected currency
  const [selectedCurrency, setSelectedCurrency] = useState<null | ElementType<
    typeof currency
  >>(null);

  // Function that clears all fields
  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setAmount('');
    setDescription('');
    setReference('');
    setSelectedRequest(null);
    setSelectedSender(null);
    setSelectedDestination(null);
    setSelectedCurrency(null);
  }, [setAmount]);

  // The state of the new transfer request
  const [state, dispatch] = useProducerWithThunks(
    newClientTransferRecipe,
    defaultAPIRequestState
  );
  // New transfer request callback for form
  const submitNewTransfer: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doNewClientTransfer({
          amount,
          currency: selectedCurrency.value,
          date,
          description,
          destination: selectedDestination.value,
          reference,
          sender: selectedSender.value,
          request: selectedRequest?.value || '',
        })
      );
    },
    [
      dispatch,
      amount,
      selectedCurrency,
      date,
      description,
      selectedDestination,
      reference,
      selectedSender,
      selectedRequest,
    ]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitNewTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Accept Amount</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <InputContainer>
          <Selector
            label="Request"
            shrinked
            options={request}
            value={selectedRequest}
            onChange={(e) => setSelectedRequest(e)}
          />
          <InputBox
            type="date"
            label="Date *"
            shrinked
            onChange={(e) => setDate(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Sender *"
            shrinked
            options={sender}
            value={selectedSender}
            onChange={(e) => setSelectedSender(e)}
          />
          <Selector
            label="Destination *"
            shrinked
            options={destination.filter((destination) => {
              if (
                selectedCurrency &&
                !destination.label.includes(selectedCurrency.value)
              )
                return false;
              return true;
            })}
            value={selectedDestination}
            onChange={(e) => setSelectedDestination(e)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label="Currency *"
            shrinked
            options={currency}
            value={selectedCurrency}
            onChange={(e) => {
              setSelectedCurrency(e);
              if (
                selectedDestination &&
                !selectedDestination.label.includes(selectedCurrency.value)
              )
                setSelectedDestination(null);
            }}
          />
          <AmountInput
            currency={selectedCurrency?.value || 'NONE'}
            shrinked
            label="Amount *"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <InputBox
            label="Description *"
            shrinked
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <InputBox
            label="Reference *"
            shrinked
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton
            type="submit"
            disabled={
              !amount ||
              !selectedCurrency ||
              !date ||
              !description ||
              !selectedDestination ||
              !reference ||
              !selectedSender
            }
          >
            Create
          </PrimaryButton>
          <SecondaryButton type="button" onClick={clearTransaction}>
            Clear
          </SecondaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export function DepositAcceptModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: DepositModal) {
  const { url, currency } = selectedRow;

  // The amount input field
  const [date, setDate] = useState('');
  const [reference, setReference] = useState('');
  const [gotAmount, setGotAmount] = useState('');

  // The state of the deposit request
  const [state, dispatch] = useProducerWithThunks(
    acceptClientTransferRecipe,
    defaultAPIRequestState
  );

  console.log(date, reference);

  // Deposit request callback for form
  const submitAcceptTransfer: React.FormEventHandler = React.useCallback(
    (e) => {
      console.log(date, reference);
      e.preventDefault();
      dispatch(
        doAcceptClientTransfer(url, {
          got_amount: gotAmount,
          got_currency: currency,
          bank_reference: reference,
          bank_date: date,
        })
      );
    },
    [dispatch, url, gotAmount, currency, reference, date]
  );

  // Function that clears all fields
  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setGotAmount('');
  }, [setGotAmount]);

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitAcceptTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Accept Amount</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <InputContainer>
          <AmountInput
            label="Got Amount *"
            currency={selectedRow.currency}
            shrinked
            value={gotAmount}
            onChange={(e) => setGotAmount(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <InputBox
            type="date"
            label="Date *"
            shrinked
            onChange={(e) => setDate(e.target.value)}
          />
          <InputBox
            label="Reference *"
            shrinked
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Create</PrimaryButton>
          <SecondaryButton type="button" onClick={clearTransaction}>
            Clear
          </SecondaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export function DepositDeclineModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: DepositModal) {
  const { url, amount, currency } = selectedRow;

  // The state of the deposit request
  const [state, dispatch] = useProducerWithThunks(
    unacceptClientTransferRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitUnacceptTransfer: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doUnacceptClientTransfer(url, {
          got_amount: amount,
          got_currency: currency,
        })
      );
    },
    [dispatch, url, amount, currency]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitUnacceptTransfer}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Decline Amount</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}
